import React from 'react';
import {
   Table,
   TableBody,
   TableCell,
   TableHead,
   TableRow,
   TablePagination,
   IconButton,
   Tooltip,
   Chip,
   Typography,
   Avatar,
   Box,
   Skeleton
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PAGINATION_CONFIG from '../../config/paginationConfig';
import { getInitialsFromEmail } from '../../utils/userUtils';
import { formatlastLogin } from '../admin/admin.functions';
import logger from '../../utils/logger';
import styles from './admin.module.css';

/**
 * User Management Table Component
 * Displays users in a table with sorting, pagination, and action buttons
 */
const UserManagementTable = ({
   users = [],
   totalUsers = 0,
   page = 0,
   rowsPerPage = 25,
   isLoading = false,
   order = 'asc',
   orderBy = 'email',
   onChangePage,
   onChangeRowsPerPage,
   onRequestSort,
   onEditUser,
   onBlockUser,
   onDeleteUser
}) => {

   logger.info('UserManagementTable render', {
      usersLength: users.length,
      totalUsers: totalUsers,
      page: page,
      rowsPerPage: rowsPerPage,
      isLoading: isLoading,
      from: page * rowsPerPage + 1,
      to: Math.min((page + 1) * rowsPerPage, totalUsers)
   });

   const handleRequestSort = (property) => {
      if (property === 'actions') return;
      
      const isAsc = orderBy === property && order === 'asc';
      const newOrder = isAsc ? 'desc' : 'asc';
      onRequestSort(property, newOrder);
   };

   const renderSortIcon = (property) => {
      if (orderBy === property) {
         return order === 'asc' ? (
            <ArrowBackIcon sx={{ transform: 'rotate(90deg)', color: '#1D9F9F', fontSize: '1.2rem' }} />
         ) : (
            <ArrowBackIcon sx={{ transform: 'rotate(-90deg)', color: '#1D9F9F', fontSize: '1.2rem' }} />
         );
      }
      return null;
   };

   const renderTableHeader = (label, property, sortable = true, centered = false) => (
      <Box 
         sx={{ 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: centered ? 'center' : 'flex-start',
            cursor: sortable ? 'pointer' : 'default'
         }}
         onClick={sortable ? () => handleRequestSort(property) : undefined}
      >
         <Typography variant="subtitle2" fontWeight="bold">{label}</Typography>
         {sortable && (
            <Box sx={{ ml: 0.5, display: 'flex', alignItems: 'center' }}>
               {renderSortIcon(property)}
            </Box>
         )}
      </Box>
   );

   const renderLoadingSkeleton = () => (
      [...Array(rowsPerPage)].map((_, index) => (
         <TableRow key={index}>
            <TableCell>
               <Box sx={{display:'flex',alignItems:'center'}}>
                  <Skeleton variant="circular" width={36} height={36}/>
               </Box>
            </TableCell>
            <TableCell><Skeleton variant="text" width={200}/></TableCell>
            <TableCell><Skeleton variant="text" width={150}/></TableCell>
            <TableCell><Skeleton variant="text" width={100}/></TableCell>
            <TableCell><Skeleton variant="text" width={80}/></TableCell>
            <TableCell align="center"><Skeleton variant="text" width={120}/></TableCell>
         </TableRow>
      ))
   );

   const renderUserRow = (user) => (
      <TableRow
         key={user.email}
         sx={{ 
            '&:hover': { backgroundColor: '#f8f9fa' },
            backgroundColor: user.isBlocked ? 'rgba(255, 0, 0, 0.05)' : 'inherit' 
         }}
      >  
         <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
               <Avatar 
                  src={user.profilePictureUrl || user.profilePicture || ''} 
                  alt={user.email.split('@')[0]}
                  sx={{ width: 36, height: 36, bgcolor: !(user.profilePictureUrl || user.profilePicture) ? '#1D9F9F' : 'inherit' }}
               >
                  {!(user.profilePictureUrl || user.profilePicture) && getInitialsFromEmail(user.email)}
               </Avatar>
            </Box>
         </TableCell>
         <TableCell>{user.email}</TableCell>
         <TableCell>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
               {user.groups && user.groups.length > 0 ? (
                  user.groups.slice(0, 3).map((group, index) => (
                     <Chip 
                        key={index} 
                        label={group} 
                        size="small" 
                        variant="outlined" 
                     />
                  ))
               ) : (
                  <Typography variant="body2" color="text.secondary">No groups</Typography>
               )}
               {user.groups && user.groups.length > 3 && (
                  <Chip 
                     label={`+${user.groups.length - 3}`} 
                     size="small" 
                     variant="outlined" 
                  />
               )}
            </Box>
         </TableCell>
         <TableCell>
            {user.lastLogin ? (
               <Tooltip title={new Date(user.lastLogin).toLocaleString('en-GB')}>
                  <Typography variant="body2">
                     {formatlastLogin(user.lastLogin)}
                  </Typography>
               </Tooltip>
            ) : (
               <Typography variant="body2" color="text.secondary">Never</Typography>
            )}
         </TableCell>
         <TableCell>
            {user.isBlocked ? (
               <Chip 
                  icon={<BlockIcon />} 
                  label="Blocked" 
                  color="error" 
                  size="small" 
                  variant="outlined" 
               />
            ) : (
               <Chip 
                  icon={<CheckCircleIcon />} 
                  label="Active" 
                  color="success" 
                  size="small" 
                  variant="outlined" 
               />
            )}
         </TableCell>
         <TableCell align="center">
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
               <Tooltip title="Edit User">
                  <IconButton 
                     className={`${styles.actionButton} ${styles.edit}`}
                     size="small"
                     onClick={() => onEditUser(user.email)}
                  >
                     <EditIcon />
                  </IconButton>
               </Tooltip>
               <Tooltip title={user.isBlocked ? "Unblock" : "Block"}>
                  <IconButton 
                     className={`${styles.actionButton} ${user.isBlocked ? styles.unblock : styles.block}`}
                     size="small"
                     onClick={() => onBlockUser(user.email, !user.isBlocked)}
                  >
                     {user.isBlocked ? <CheckCircleIcon /> : <BlockIcon />}
                  </IconButton>
               </Tooltip>
               <Tooltip title="Delete">
                  <IconButton 
                     className={`${styles.actionButton} ${styles.delete}`}
                     size="small" 
                     onClick={() => onDeleteUser(user.email)}
                  >
                     <DeleteIcon />
                  </IconButton>
               </Tooltip>
            </Box>
         </TableCell>
      </TableRow>
   );

   return (
      <>
         {/* Top pagination controls */}
         <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
           <TablePagination
             rowsPerPageOptions={PAGINATION_CONFIG.PAGE_SIZE_OPTIONS}
             component="div"
             count={totalUsers}
             rowsPerPage={rowsPerPage}
             page={page}
             onPageChange={(event, newPage) => {
               onChangePage(event, newPage);
             }}
             onRowsPerPageChange={(event) => {
               onChangeRowsPerPage(event);
             }}
             labelDisplayedRows={
               ({ from, to, count }) => {
                 const label = `Showing ${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
                 return label;
               }
             }
           />
         </Box>
         
         <Table sx={{ minWidth: 650 }}>
            <TableHead>
               <TableRow sx={{ backgroundColor: '#f5f5f5' }}>          
                  <TableCell>{renderTableHeader('Profile', 'profilePicture')}</TableCell>
                  <TableCell>{renderTableHeader('Email', 'email')}</TableCell>
                  <TableCell>{renderTableHeader('Groups', 'groups')}</TableCell>
                  <TableCell>{renderTableHeader('Last Login', 'lastLogin')}</TableCell>
                  <TableCell>{renderTableHeader('Status', 'isBlocked')}</TableCell>
                  <TableCell align='center'>{renderTableHeader('Actions', 'actions', false, true)}</TableCell>
               </TableRow>
            </TableHead>
            <TableBody>
               {isLoading ? renderLoadingSkeleton() : users.map(renderUserRow)}
            </TableBody>
         </Table>
         
         <TablePagination
            rowsPerPageOptions={PAGINATION_CONFIG.PAGE_SIZE_OPTIONS}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => {
              onChangePage(event, newPage);
            }}
            onRowsPerPageChange={(event) => {
              onChangeRowsPerPage(event);
            }}
            labelDisplayedRows={
              ({ from, to, count }) => {
                const label = `Showing ${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
                return label;
              }
            }
         />
      </>
   );
};

export default UserManagementTable;
